import type { PartialPropsOf, PropsOf, ReactNode } from "react";
import { forwardRef } from "react";
import { graphql, useFragment } from "react-relay";

import type { Targeting } from "~/components/advertisement/ad-slots/types";
import { ContentActionBar } from "~/components/content/content-action-bar";
import { ContentAuthorImage } from "~/components/content/content-author-image";
import { ContentAuthorLink } from "~/components/content/content-author-link";
import { ContentCoverImage } from "~/components/content/content-cover-image";
import { ContentCoverVideo } from "~/components/content/content-cover-video";
import { ContentHeadline } from "~/components/content/content-headline";
import { ContentItemMagazinesStyleArticleMoreOnThis } from "~/components/content/content-item-render/variants/magazines-style-article-more-on-this";
import { ContentLiveTag } from "~/components/content/content-live-tag";
import { ContentPrintHeadline } from "~/components/content/content-print-headline";
import { ContentPublishedDate } from "~/components/content/content-published-date";
import { ContentSections } from "~/components/content/content-sections";
import { useContentSponsor } from "~/components/content/content-sponsor-tag/hooks";
import { ContentSubHeadline } from "~/components/content/content-sub-headline";
import { ContentSummary } from "~/components/content/content-summary";
import { ContentTopicLink } from "~/components/content/content-topic-link";
import { SponsorTag } from "~/components/sponsor-tag";
import { VideoDurationOverlay } from "~/components/video/video-duration-overlay";
import type { contentItemRenderContent$key } from "~/queries/__generated__/contentItemRenderContent.graphql";
import type { CustomQueryParameters } from "~/types/gtm";

import { withHighlightedHeadline } from "./hoc";
import { ContentItemCard } from "./variants/card";
import { ContentItemHomeAdvertiser } from "./variants/home-advertiser";
import { ContentItemHomeAroundMorningStudioPrimary } from "./variants/home-around-morning-studio-primary";
import { ContentItemHomeAroundMorningStudioSecondary } from "./variants/home-around-morning-studio-secondary";
import { ContentItemHomeAroundPrimary } from "./variants/home-around-primary";
import { ContentItemHomeAroundSecondary } from "./variants/home-around-secondary";
import { ContentItemHomeComment } from "./variants/home-comment";
import { ContentItemDailyFive } from "./variants/home-daily-five";
import { ContentItemHomeFocus } from "./variants/home-focus";
import { ContentItemHomeHarry } from "./variants/home-harry";
import { ContentItemHomeHeroArticle } from "./variants/home-hero-article";
import { ContentItemHomeLatest } from "./variants/home-latest";
import { ContentItemHomeMultimedia } from "./variants/home-multimedia";
import { ContentItemHomeMultimediaPodcast } from "./variants/home-multimedia-podcast";
import type { ContentItemHomePrimaryVariantProps } from "./variants/home-primary";
import { ContentItemHomePrimary } from "./variants/home-primary";
import type { ContentItemHomeSecondaryVariantProps } from "./variants/home-secondary";
import { ContentItemHomeSecondary } from "./variants/home-secondary";
import type { ContentItemHomeSectionLeadPrimaryVariantProps } from "./variants/home-section-lead-primary";
import { ContentItemHomeSectionLeadPrimary } from "./variants/home-section-lead-primary";
import type { ContentItemHomeSectionLeadSecondaryVariantProps } from "./variants/home-section-lead-secondary";
import { ContentItemHomeSectionLeadSecondary } from "./variants/home-section-lead-secondary";
import { ContentItemHomeStoryPackageLead } from "./variants/home-story-package-lead";
import { ContentItemHomeStoryPackageTail } from "./variants/home-story-package-tail";
import { ContentItemHomeSuperSplashLargePrimary } from "./variants/home-super-splash-large-primary";
import { ContentItemHomeSuperSplashLivePrimary } from "./variants/home-super-splash-live-primary";
import type { ContentItemHomeSuperSplashLiveSecondaryVariantProps } from "./variants/home-super-splash-live-secondary";
import { ContentItemHomeSuperSplashLiveSecondary } from "./variants/home-super-splash-live-secondary";
import { ContentItemLivePrimary } from "./variants/live-primary";
import {
  ContentItemMagazinesStyleCardSquare,
  ContentItemMagazinesStyleCardWide,
} from "./variants/magazines-style-card";
import { ContentItemStyleFashionCarousel } from "./variants/magazines-style-fashion-carousel";
import type { ContentItemMagazinesStyleLeadVariantProps } from "./variants/magazines-style-lead";
import { ContentItemMagazinesStyleLead } from "./variants/magazines-style-lead";
import { ContentItemMagazinesStyleSpotlight } from "./variants/magazines-style-spotlight";
import { ContentItemMorningStudio } from "./variants/morning-studio";
import { ContentItemOpinion } from "./variants/opinion";
import { ContentItemPostiesPrimary } from "./variants/posties-primary";
import { ContentItemPostiesSecondary } from "./variants/posties-secondary";
import { ContentItemPostiesSectionPrimary } from "./variants/posties-section-primary";
import { ContentItemPostiesSectionSecondary } from "./variants/posties-section-secondary";
import { ContentItemPostiesTodaysRead } from "./variants/posties-todays-read";
import { ContentItemPrimary } from "./variants/primary";
import { ContentItemSearch } from "./variants/search";
import { ContentItemSecondary } from "./variants/secondary";

export type Props = {
  advertisement?: {
    targeting: Targeting;
    zone: string;
  };
  children?: ReactNode;
  className?: string;
  customQueryParameters?: CustomQueryParameters;
  onClick?: (entityId?: string) => void;
  reference: contentItemRenderContent$key;
  variant: Variant;
  withAuthorImage?: boolean;
  withAuthorLink?: boolean;
  withTopicLink?: Omit<PropsOf<typeof ContentTopicLink>, "reference">;
} & (
  | {
      variant: Exclude<
        Variant,
        | "magazines-style-lead"
        | "home-secondary"
        | "home-section-lead-primary"
        | "home-section-lead-secondary"
        | "home-super-splash-live-secondary"
      >;
    }
  | ({
      variant: "magazines-style-lead";
    } & ContentItemMagazinesStyleLeadVariantProps)
  | ({
      variant: "home-primary";
    } & ContentItemHomePrimaryVariantProps)
  | ({
      variant: "home-secondary";
    } & ContentItemHomeSecondaryVariantProps)
  | ({
      variant: "home-section-lead-primary";
    } & ContentItemHomeSectionLeadPrimaryVariantProps)
  | ({
      variant: "home-section-lead-secondary";
    } & ContentItemHomeSectionLeadSecondaryVariantProps)
  | ({
      variant: "home-super-splash-live-secondary";
    } & ContentItemHomeSuperSplashLiveSecondaryVariantProps)
);

type Variant =
  | "card"
  | "home-advertiser"
  | "home-around-morning-studio-primary"
  | "home-around-morning-studio-secondary"
  | "home-around-primary"
  | "home-around-secondary"
  | "home-comment"
  | "home-daily-five"
  | "home-focus"
  | "home-harry"
  | "home-hero-article"
  | "home-latest"
  | "home-multimedia-podcast"
  | "home-multimedia"
  | "home-primary"
  | "home-secondary"
  | "home-section-lead-primary"
  | "home-section-lead-secondary"
  | "home-story-package-lead"
  | "home-story-package-tail"
  | "home-super-splash-large-primary"
  | "home-super-splash-live-primary"
  | "home-super-splash-live-secondary"
  | "live-primary"
  | "magazines-style-article-more-on-this"
  | "magazines-style-card-square"
  | "magazines-style-card-wide"
  | "magazines-style-fashion-carousel"
  | "magazines-style-lead"
  | "magazines-style-spotlight"
  | "morning-studio"
  | "opinion"
  | "posties-primary"
  | "posties-secondary"
  | "posties-section-primary"
  | "posties-section-secondary"
  | "posties-todays-read"
  | "primary"
  | "search"
  | "secondary";

export type DefaultComponentsProvider = {
  actionBar: (props?: PartialPropsOf<typeof ContentActionBar>) => ReactNode;
  authorImage?: (props?: PartialPropsOf<typeof ContentAuthorImage>) => ReactNode;
  authorLink?: (props?: PartialPropsOf<typeof ContentAuthorLink>) => ReactNode;
  coverImage: (props?: PartialPropsOf<typeof ContentCoverImage>) => ReactNode;
  coverVideo: (props?: PartialPropsOf<typeof ContentCoverVideo>) => ReactNode;
  headline: (props?: PartialPropsOf<typeof ContentHeadline>) => ReactNode;
  liveTag: (props?: PartialPropsOf<typeof ContentLiveTag>) => ReactNode;
  printHeadline: (props?: PartialPropsOf<typeof ContentPrintHeadline>) => ReactNode;
  publishedDate: (props?: PartialPropsOf<typeof ContentPublishedDate>) => ReactNode;
  sections: (props?: PartialPropsOf<typeof ContentSections>) => ReactNode;
  sponsorTag?: (props?: PartialPropsOf<typeof SponsorTag>) => ReactNode;
  subHeadline: (props?: PartialPropsOf<typeof ContentSubHeadline>) => ReactNode;
  summary: (props?: PartialPropsOf<typeof ContentSummary>) => ReactNode;
  topicLink?: (props?: PartialPropsOf<typeof ContentTopicLink>) => ReactNode;
  video?: {
    durationOverlay: (props?: PartialPropsOf<typeof VideoDurationOverlay>) => ReactNode;
  };
};

const Component = forwardRef<HTMLDivElement, Props>(
  (
    {
      advertisement,
      children,
      className,
      customQueryParameters,
      onClick,
      reference: reference_,
      variant,
      withAuthorImage,
      withAuthorLink,
      withTopicLink,
      ...variantProps
    },
    reference,
  ) => {
    const content = useFragment(
      graphql`
        fragment contentItemRenderContent on Content {
          ... on Video {
            __typename
            ...videoDurationOverlayVideo
          }
          ...cardContentItemContent
          ...contentActionBarContent
          ...contentAuthorImageContent
          ...contentAuthorLinkContent
          ...contentCoverImageContent
          ...contentCoverVideoContent
          ...contentHeadlineContent
          ...contentLiveTagContent
          ...contentPrintHeadlineContent
          ...contentPublishedDateContent
          ...contentSectionsContent
          ...contentSubHeadlineContent
          ...contentSummaryContent
          ...contentTopicLinkContent
          ...entityLink
          ...homeAdvertiserContentItemContent
          ...homeAroundMorningStudioPrimaryContentItemContent
          ...homeAroundMorningStudioSecondaryContentItemContent
          ...homeAroundPrimaryContentItemContent
          ...homeAroundSecondaryContentItemContent
          ...homeCommentContentItemContent
          ...homeDailyFiveContentItemContent
          ...homeFocusContentItemContent
          ...homeHarryContentItemContent
          ...homeHeroArticleContentItemContent
          ...homeLatestContentItemContent
          ...homeMultimediaContentItemContent
          ...homeMultimediaPodcastContentItemContent
          ...homePrimaryContentItemContent
          ...homeSecondaryContentItemContent
          ...homeSectionLeadPrimaryContentItemContent
          ...homeSectionLeadSecondaryContentItemContent
          ...homeStoryPackageLeadContentItemContent
          ...homeStoryPackageTailContentItemContent
          ...homeSuperSplashLargePrimaryContentItemContent
          ...homeSuperSplashLivePrimaryContentItemContent
          ...homeSuperSplashLiveSecondaryContentItemContent
          ...hooksUseContentSponsorContent
          ...livePrimaryContentItemContent
          ...magazinesStyleArticleMoreOnThisContentItemContent
          ...magazinesStyleCardContentItemContent
          ...magazinesStyleFashionCarouselContentItemContent
          ...magazinesStyleLeadContentItemContent
          ...magazinesStyleSpotlightContentItemContent
          ...morningStudioContentItemContent
          ...opinionContentItemContent
          ...postiesPrimaryContentItemContent
          ...postiesSecondaryContentItemContent
          ...postiesSectionPrimaryContentItemContent
          ...postiesSectionSecondaryContentItemContent
          ...postiesTodaysReadContentItemContent
          ...primaryContentItemContent
          ...searchContentItemContent
          ...secondaryContentItemContent
        }
      `,
      reference_,
    );
    const contentSponsor = useContentSponsor(content);
    if (!content) return null;

    const provider: DefaultComponentsProvider = {
      actionBar: props => <ContentActionBar reference={content} {...props} />,
      coverImage: props => (
        <ContentCoverImage
          reference={content}
          responsiveVariants={{
            desktopUp: "size540x360",
            mobileUp: "size540x360",
            tabletUp: "size540x360",
          }}
          {...props}
        />
      ),
      coverVideo: props => <ContentCoverVideo reference={content} {...props} />,
      headline: props => (
        <ContentHeadline
          reference={content}
          useContentSponsorStyle={!!contentSponsor?.name}
          {...props}
        />
      ),
      liveTag: props => <ContentLiveTag reference={content} {...props} />,
      printHeadline: props => <ContentPrintHeadline reference={content} {...props} />,
      publishedDate: props => <ContentPublishedDate reference={content} {...props} />,
      sections: props => <ContentSections reference={content} {...props} />,
      subHeadline: props => <ContentSubHeadline reference={content} {...props} />,
      summary: props => <ContentSummary reference={content} {...props} />,
    };
    if (withAuthorLink) {
      provider.authorLink = props => <ContentAuthorLink reference={content} {...props} />;
    }
    if (withAuthorImage) {
      provider.authorImage = props => <ContentAuthorImage reference={content} {...props} />;
    }
    if (withTopicLink) {
      provider.topicLink = props => (
        <ContentTopicLink {...withTopicLink} {...props} reference={content} />
      );
    }
    if (content.__typename === "Video") {
      provider.video = {
        durationOverlay: props => (
          <VideoDurationOverlay
            reference={content}
            responsiveVariants={{ desktopUp: "medium", mobileUp: "small", tabletUp: "medium" }}
            variant="default"
            {...props}
          />
        ),
      };
    }

    if (contentSponsor?.name) {
      provider.sponsorTag = props => <SponsorTag reference={contentSponsor} {...props} />;
    }

    const ContentItemComponentMap = {
      card: ContentItemCard,
      "home-advertiser": ContentItemHomeAdvertiser,
      "home-around-morning-studio-primary": ContentItemHomeAroundMorningStudioPrimary,
      "home-around-morning-studio-secondary": ContentItemHomeAroundMorningStudioSecondary,
      "home-around-primary": ContentItemHomeAroundPrimary,
      "home-around-secondary": ContentItemHomeAroundSecondary,
      "home-comment": ContentItemHomeComment,
      "home-daily-five": ContentItemDailyFive,
      "home-focus": ContentItemHomeFocus,
      "home-harry": ContentItemHomeHarry,
      "home-hero-article": ContentItemHomeHeroArticle,
      "home-latest": ContentItemHomeLatest,
      "home-multimedia": ContentItemHomeMultimedia,
      "home-multimedia-podcast": ContentItemHomeMultimediaPodcast,
      "home-primary": ContentItemHomePrimary,
      "home-secondary": ContentItemHomeSecondary,
      "home-section-lead-primary": ContentItemHomeSectionLeadPrimary,
      "home-section-lead-secondary": ContentItemHomeSectionLeadSecondary,
      "home-story-package-lead": ContentItemHomeStoryPackageLead,
      "home-story-package-tail": ContentItemHomeStoryPackageTail,
      "home-super-splash-large-primary": ContentItemHomeSuperSplashLargePrimary,
      "home-super-splash-live-primary": ContentItemHomeSuperSplashLivePrimary,
      "home-super-splash-live-secondary": ContentItemHomeSuperSplashLiveSecondary,
      "live-primary": ContentItemLivePrimary,
      "magazines-style-article-more-on-this": ContentItemMagazinesStyleArticleMoreOnThis,
      "magazines-style-card-square": ContentItemMagazinesStyleCardSquare,
      "magazines-style-card-wide": ContentItemMagazinesStyleCardWide,
      "magazines-style-fashion-carousel": ContentItemStyleFashionCarousel,
      "magazines-style-lead": ContentItemMagazinesStyleLead,
      "magazines-style-spotlight": ContentItemMagazinesStyleSpotlight,
      "morning-studio": ContentItemMorningStudio,
      opinion: ContentItemOpinion,
      "posties-primary": ContentItemPostiesPrimary,
      "posties-secondary": ContentItemPostiesSecondary,
      "posties-section-primary": ContentItemPostiesSectionPrimary,
      "posties-section-secondary": ContentItemPostiesSectionSecondary,
      "posties-todays-read": ContentItemPostiesTodaysRead,
      primary: ContentItemPrimary,
      search: ContentItemSearch,
      secondary: ContentItemSecondary,
    };

    const ContentItemComponent = ContentItemComponentMap[variant];
    const itemProps = {
      advertisement,
      children,
      className,
      customQueryParameters,
      onClick,
      provider,
      ref: reference,
      reference: content,
      variantProps,
    };

    return <ContentItemComponent {...itemProps} />;
  },
);

Component.displayName = "ContentItemRender";

export const ContentItemRender = withHighlightedHeadline(Component);
